import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, HostListener, ViewChildren, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { debounceTime, distinctUntilChanged, Subject, switchMap, of } from 'rxjs';
import {
  ApiService

} from 'src/app/services/api/api.service';
@Component({
  selector: 'app-chat-room-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TextareaModule,
    ToastModule,
    TooltipModule,
    CheckboxModule
  ],
  providers: [MessageService],
  templateUrl: './chat-room-input.component.html',
  styleUrls: ['./chat-room-input.component.scss']
})
export class ChatRoomInputComponent {
  @Input() loading = false;
  @Input() currentRoom: any;
  @Input() context: any;
  @Output() messageSent = new EventEmitter<any>();

  message = '';
  isPriority = false;

  // Tagging
  tagQuery$ = new Subject<{ type: 'user' | 'doc'; query: string }>();
  suggestions: any[] = [];
  showSuggestions = false;
  tagType: 'user' | 'doc' | null = null;
  tagStartIndex: number | null = null;
  selectedSuggestionIndex = 0;

  private allUsers: any[] = [];

  @ViewChild('textareaRef') textareaRef!: ElementRef<HTMLTextAreaElement>;
  @ViewChildren('suggestionItem') suggestionItems!: QueryList<ElementRef>;
  @ViewChild('suggestionsContainer') suggestionsContainer!: ElementRef;
  
  focusInput(): void {
    this.textareaRef?.nativeElement.focus();
  }

  constructor(private messageService: MessageService, private apiService: ApiService) {
    // Debounced tagging search
    this.tagQuery$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap(({ type, query }) => {
          const companyId = this.currentRoom?.company_id || 1;

          if (type === 'user') {
            // Get all users only once
            if (this.allUsers.length === 0) {
              return this.apiService.getCompanyUsers(companyId);
            }
            return of(this.allUsers);
          } else {
            // getCompanyDocumentsLight(company_id, statut, page?, type?, searchTerm?, per_page?)
            return this.apiService.getCompanyDocumentsLight(companyId, 2, 1, undefined, query, 5);
          }
        })
      )
      .subscribe((results: any) => {
        if (this.tagType === 'user') {
          this.allUsers = results || [];
          const query = this.getCurrentTagQuery();
          this.suggestions = this.filterUsers(query);
        } else {
          this.suggestions = results?.data || [];
        }
        this.showSuggestions = this.suggestions.length > 0;
        this.selectedSuggestionIndex = 0;
      });
  }

  sendMessage() {
    if (!this.message.trim()) return;

    if (!this.currentRoom) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Aucun salon sélectionné'
      });
      return;
    }

    this.messageSent.emit({
      message: this.getMessageForApi(),
      isPriority: this.isPriority
    });

    this.message = '';
    this.isPriority = false;
    this.suggestions = [];
    this.showSuggestions = false;

    setTimeout(() => this.focusInput(), 0);
  }

  handleKeyDown(event: KeyboardEvent): void {
    const textarea = this.textareaRef.nativeElement;
    const cursorPos = textarea.selectionStart;
  
    // 🟢 Handle deleting entire tags
    if (event.key === 'Backspace' || event.key === 'Delete') {
      const tagInfo = this.findTagAtCursor(cursorPos);
      if (tagInfo) {
        event.preventDefault();
  
        // Remove the tag text from the message
        this.message =
          this.message.slice(0, tagInfo.start) + this.message.slice(tagInfo.end);
  
        // Remove from corresponding map
        if (tagInfo.type === 'user') this.userTags.delete(tagInfo.tag);
        else this.docTags.delete(tagInfo.tag);
  
        // Restore cursor position properly
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = tagInfo.start;
          textarea.focus();
        });
  
        return; // stop further handling
      }
    }
  
    // Existing suggestion handling logic (unchanged)
    if (!this.showSuggestions) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
      return;
    }
  
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.selectedSuggestionIndex < this.suggestions.length - 1) {
        this.selectedSuggestionIndex++;
        this.scrollToActiveSuggestion();
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.selectedSuggestionIndex > 0) {
        this.selectedSuggestionIndex--;
        this.scrollToActiveSuggestion();
      }
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      const selected = this.suggestions[this.selectedSuggestionIndex];
      if (selected) this.selectSuggestion(selected);
    }
  }

  private findTagAtCursor(cursorPos: number): { tag: string, type: 'user' | 'doc', start: number, end: number } | null {
    const allTags: { tag: string, type: 'user' | 'doc' }[] = [
      ...Array.from(this.userTags.keys()).map(tag => ({ tag, type: 'user' as const })),
      ...Array.from(this.docTags.keys()).map(tag => ({ tag, type: 'doc' as const }))
    ];
  
    for (const { tag, type } of allTags) {
      const start = this.message.indexOf(tag);
      if (start === -1) continue;
      const end = start + tag.length;
      if (cursorPos > start && cursorPos <= end) {
        return { tag, type, start, end };
      }
    }
    return null;
  }
  


  private scrollToActiveSuggestion(): void {
    const items = this.suggestionItems.toArray();
    const active = items[this.selectedSuggestionIndex];
    const container = this.suggestionsContainer?.nativeElement;

    if (active && container) {
      const activeEl = active.nativeElement;
      const containerRect = container.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();

      if (activeRect.bottom > containerRect.bottom) {
        container.scrollTop += activeRect.bottom - containerRect.bottom;
      } else if (activeRect.top < containerRect.top) {
        container.scrollTop -= containerRect.top - activeRect.top;
      }
    }
  }


  handleInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    const cursor = textarea.selectionStart;
    const textBefore = this.message.slice(0, cursor);
  
    this.checkTagDeletion();
  
    const match = /(^|\s)([@#])([\wÀ-ÿ]*)$/.exec(textBefore);
  
    if (match) {
      const symbol = match[2];
      const query = match[3] || '';
      this.tagType = symbol === '@' ? 'user' : 'doc';
      this.tagStartIndex = cursor - query.length - 1;
  
      // 🟣 Cas spécial : @everyone
      if (this.tagType === 'user' && query.toLowerCase().startsWith('every')) {
        this.suggestions = [
          { id: 'everyone', firstname: '', name: 'everyone', username: 'everyone' }
        ];
        this.showSuggestions = true;
        this.selectedSuggestionIndex = 0;
        return;
      }
  
      this.tagQuery$.next({ type: this.tagType, query });
    } else {
      this.showSuggestions = false;
    }
  }
  
  

  private checkTagDeletion(): void {
    // Vérifie si un tag existant a été partiellement supprimé
    const tagsToDelete: string[] = [];

    // 🔵 Pour les tags user
    this.userTags.forEach((_id, tagText) => {
      if (!this.message.includes(tagText)) {
        tagsToDelete.push(tagText);
      } else {
        // Si on tape dans le tag sans qu'il soit complet
        const regex = new RegExp(`\\B${this.escapeRegex(tagText)}\\b`);
        if (!regex.test(this.message)) tagsToDelete.push(tagText);
      }
    });

    // 🔵 Pour les tags doc
    this.docTags.forEach((_id, tagText) => {
      if (!this.message.includes(tagText)) {
        tagsToDelete.push(tagText);
      } else {
        const regex = new RegExp(`\\B${this.escapeRegex(tagText)}\\b`);
        if (!regex.test(this.message)) tagsToDelete.push(tagText);
      }
    });

    // 🧹 Supprimer les tags trouvés et les effacer du message proprement
    for (const tag of tagsToDelete) {
      this.userTags.delete(tag);
      this.docTags.delete(tag);
      const regex = new RegExp(this.escapeRegex(tag), 'g');
      this.message = this.message.replace(regex, '');
    }
  }



  private userTags = new Map<string, string>(); // displayName => id
  private docTags = new Map<string, string>();  // title => id

  selectSuggestion(item: any): void {
    if (this.tagStartIndex == null) return;
    const textarea = this.textareaRef.nativeElement;
    const cursor = textarea.selectionStart;
  
    const before = this.message.slice(0, this.tagStartIndex);
    const after = this.message.slice(cursor);
  
    let tagText = '';
  
    if (this.tagType === 'user') {
      if (item.id === 'everyone') {
        tagText = '@everyone'; // 💡 Pas de tracking spécial
      } else {
        tagText = `@${item.firstname} ${item.name}`;
        this.userTags.set(tagText, item.id);
      }
    } else {
      tagText = `#${item.title}`;
      this.docTags.set(tagText, item.id);
    }
  
    this.message = `${before}${tagText} ${after}`;
    this.showSuggestions = false;
    this.tagStartIndex = null;
  
    setTimeout(() => textarea.focus());
  }
  
  

  private getMessageForApi(): string {
    let msg = this.message;
  
    this.userTags.forEach((id, displayNameWithAt) => {
      if (displayNameWithAt === '@everyone') return; // 🚫 ne pas toucher
  
      const displayName = displayNameWithAt.startsWith('@') ? displayNameWithAt.slice(1) : displayNameWithAt;
      const escaped = this.escapeRegex(displayNameWithAt);
      const regex = new RegExp(escaped, 'g');
      msg = msg.replace(regex, `@[${displayName}](user:${id})`);
    });
  
    this.docTags.forEach((id, tagText) => {
      const title = tagText.startsWith('#') ? tagText.slice(1) : tagText;
      const escaped = this.escapeRegex(tagText);
      const regex = new RegExp(escaped, 'g');
      msg = msg.replace(regex, `#[${title}](doc:${id})`);
    });
  
    return msg;
  }
  
  


  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }





  // Helper: extract current partial tag text
  private getCurrentTagQuery(): string {
    const textarea = this.textareaRef?.nativeElement;
    if (!textarea) return '';
    const cursor = textarea.selectionStart;
    const textBefore = this.message.slice(0, cursor);
    const match = /(^|\s)([@#])([\wÀ-ÿ]*)$/.exec(textBefore);
    return match?.[3] || '';
  }

  private filterUsers(query: string): any[] {
    if (!query) return this.allUsers;
    const q = query.toLowerCase();
    return this.allUsers.filter(u =>
      `${u.firstname} ${u.name} ${u.username}`.toLowerCase().includes(q)
    );
  }

  get highlightedMessage(): string {
    if (!this.message) return '';
  
    // 🔒 Échappe le HTML pour éviter les injections
    const escapeHtml = (str: string) =>
      str.replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
  
    let highlighted = escapeHtml(this.message);
  
    // 🟣 Cas spécial : @everyone
    highlighted = highlighted.replace(
      /(^|\s)(@everyone)\b/g,
      `$1<span class="text-purple-600 bg-purple-100 rounded-sm font-semibold">@everyone</span>`
    );
  
    // 🔵 Surligne les utilisateurs taggés
    this.userTags.forEach((id, displayName) => {
      const escaped = escapeHtml(displayName);
      const regex = new RegExp(
        escaped.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'),
        'g'
      );
      highlighted = highlighted.replace(
        regex,
        `<span class="text-blue-500 bg-blue-100 rounded-sm">${escaped}</span>`
      );
    });
  
    // 🟢 Surligne les documents
    this.docTags.forEach((id, tagText) => {
      const escaped = escapeHtml(tagText);
      const regex = new RegExp(
        escaped.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'),
        'g'
      );
      highlighted = highlighted.replace(
        regex,
        `<span class="text-green-600 bg-green-100 rounded-sm">${escaped}</span>`
      );
    });
  
    return highlighted;
  }
  


}
